import { useFormik } from "formik";
import Select from "../../../components/select";
import { useHealthMonitorList, usePoolList, useSubnetList } from "../layout/resuableEffect";
import * as Yup from "yup";
import Input from "../../../components/input";
import arrow from "../../../assets/svg-icons/blueArrow.svg";
import { useDispatch, useSelector } from "react-redux";
import { createNetworkSubnet, listLoadBalancer, deleteHealthMonitor, createLoadBalancer, createLoadListener, updateLoadListener, listLoadPool, createHealthMonitor, updateHealthMonitor } from "../../../hooks/local/userReducer";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { FormatDate } from "../../../hooks/constants";
import close from "../../../assets/svg-icons/closeBrand.svg";
import Modal from "../../../components/modal";
import TabContent from "../../../components/TabContent";
import Button from "../../../components/button";
import LoadBalancerMembers from "./loadBalancerMembers";
import { useMemo } from "react";


const PoolDetails = () => {

  document.title = "Pool Details - Cloudvantage";


  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const selectedPool = JSON.parse(sessionStorage.getItem("selectedPool"));

  const [loadPool, setLoadPool] = useState(null);
  const [isFetched, setIsFetched] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [manageHealthMonitorModal, setManageHealthMonitorModal] = useState(false);
  const [createHealthMonitorModal, setCreateHealthMonitorModal] = useState(false);
  const [editHealthMonitorModal, setEditHealthMonitorModal] = useState(false);
  const [deleteHealthMonitorModal, setDeleteHealthMonitorModal] = useState(false);
  const [healthMonitorDetailsModal, setHealthMonitorDetailsModal] = useState(false);
  const [selectedHealthMonitor, setSelectedMonitor] = useState(null);

  const projectId = selectedProject?.id;

  // Memoize handleGetListener with useCallback
  const handleGetListener = useCallback(async () => {
    if (!selectedPool?.poolId || !selectedProject?.id) {
      return;
    }

    const poolId = selectedPool?.poolId;

    const { payload } = await dispatch(listLoadPool({ projectId, poolId }));
    console.log(payload)
    setLoadPool(payload);
    setIsFetched(true);
    
  }, [selectedPool, selectedProject, dispatch, projectId]);

  useEffect(() => {
    if (!isFetched && selectedPool && selectedProject) {
      handleGetListener();
    }
  }, [isFetched, selectedPool, selectedProject, handleGetListener]);

  const [selectedTab, setSelectedTab] = useState("Overview");

  const networkTabs = ["Overview", "Health Monitors", "Members"];

  const toggleCreateHealthMonitorModal = () => {
    setCreateHealthMonitorModal(!createHealthMonitorModal);
  };

  const poolList = usePoolList(projectId);

  const poolOptions = Array.isArray(poolList)
    ? [
        { label: "Select a pool", value: "" },
        ...poolList.map((pool) => ({
          value: pool.id,
          label: pool.name || `Pool ${pool.id}`,
        })),
      ]
    : [{ label: "Select a pool", value: "" }];

  const createHealthMonitorForm = useFormik({
    initialValues: {
      createHealthMonitor: "yes", 
      type: "",
      delay: "5",
      name: "",
      max_retries: "3",
      max_retries_down: "3",
      timeout: "5",
      pool_id: "",
      http_method: "GET", 
      expected_codes: "200", 
      url_path: "/", 
      http_version: "1.0", 
    },
    validationSchema: Yup.object({
      createHealthMonitor: Yup.string()
        .required("Please select whether to create a health monitor")
        .oneOf(["yes", "no"], "Invalid choice"),
      type: Yup.string().when("createHealthMonitor", {
        is: "yes",
        then: (schema) =>
          schema
            .required("Type is required")
            .oneOf(
              ["HTTP", "HTTPS", "PING", "SCTP", "TCP", "TLS-HELLO", "UDP-CONNECT"],
              "Invalid monitor type"
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
      delay: Yup.number().when("createHealthMonitor", {
        is: "yes",
        then: (schema) =>
          schema
            .required("Delay is required")
            .min(1, "Delay must be at least 1 second")
            .integer("Delay must be an integer")
            .test(
            "delay-gte-timeout",
            "Delay must be greater than or equal to Timeout",
            function (value) {
              const { timeout } = this.parent;
              return value >= parseInt(timeout);
            }
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      name: Yup.string().when("createHealthMonitor", {
        is: "yes",
        then: (schema) => schema.required("Name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      max_retries: Yup.number().when("createHealthMonitor", {
        is: "yes",
        then: (schema) =>
          schema
            .required("Max retries is required")
            .min(1, "Max retries must be at least 1")
            .max(10, "Max retries cannot exceed 10")
            .integer("Max retries must be an integer"),
        otherwise: (schema) => schema.notRequired(),
      }),
      max_retries_down: Yup.number().when("createHealthMonitor", {
        is: "yes",
        then: (schema) =>
          schema
            .required("Max retries down is required")
            .min(1, "Max retries down must be at least 1")
            .integer("Max retries down must be an integer"),
        otherwise: (schema) => schema.notRequired(),
      }),
      timeout: Yup.number().when("createHealthMonitor", {
        is: "yes",
        then: (schema) =>
          schema
            .required("Timeout is required")
            .min(1, "Timeout must be at least 1 second")
            .integer("Timeout must be an integer"),
        otherwise: (schema) => schema.notRequired(),
      }),
      http_method: Yup.string().when(["createHealthMonitor", "type"], {
        is: (createHealthMonitor, type) =>
          createHealthMonitor === "yes" && ["HTTP", "HTTPS"].includes(type),
        then: (schema) => schema.required("HTTP Method is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      expected_codes: Yup.string().when(["createHealthMonitor", "type"], {
        is: (createHealthMonitor, type) =>
          createHealthMonitor === "yes" && ["HTTP", "HTTPS"].includes(type),
        then: (schema) => schema.required("Expected Codes are required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      url_path: Yup.string().when(["createHealthMonitor", "type"], {
        is: (createHealthMonitor, type) =>
          createHealthMonitor === "yes" && ["HTTP", "HTTPS"].includes(type),
        then: (schema) => schema.required("URL Path is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      http_version: Yup.string().when(["createHealthMonitor", "type"], {
        is: (createHealthMonitor, type) =>
          createHealthMonitor === "yes" && ["HTTP", "HTTPS"].includes(type),
        then: (schema) =>
          schema
            .required("HTTP Version is required")
            .oneOf(["1.0", "1.1"], "HTTP Version must be 1.0 or 1.1"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (values.createHealthMonitor === "no") {
        toggleCreateHealthMonitorModal();
        return;
      }

      const createHealthMonitorFormData = {
        projectId,
        type: values.type,
        delay: parseInt(values.delay),
        name: values.name,
        max_retries: parseInt(values.max_retries),
        max_retries_down: parseInt(values.max_retries_down), 
        timeout: parseInt(values.timeout),
        pool_id: values.pool_id,
        http_method: ["HTTP", "HTTPS"].includes(values.type) ? values.http_method : undefined,
        expected_codes: ["HTTP", "HTTPS"].includes(values.type) ? values.expected_codes : undefined,
        url_path: ["HTTP", "HTTPS"].includes(values.type) ? values.url_path : undefined,
        http_version: ["HTTP", "HTTPS"].includes(values.type) ? values.http_version : undefined,
      };

      const { payload } = await dispatch(createHealthMonitor(createHealthMonitorFormData));
      if (payload.statuscode === "00") {
        await dispatch(listLoadPool({ projectId, poolId: values.pool_id }));
        toggleCreateHealthMonitorModal();
        resetForm();
        window.location.reload();
      }
    },
  });

  const createHealthMonitorInputs = useMemo(
    () => [
      {
        type: "choice",
        element: "choice",
        name: "createHealthMonitor",
        label: "Create Health Monitor? *",
        choices: [
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" },
        ],
        value: createHealthMonitorForm.values.createHealthMonitor,
        colSpan: 2,
        onChange: (value) => createHealthMonitorForm.setFieldValue("createHealthMonitor", value),
        onBlur: createHealthMonitorForm.handleBlur,
        onError:
          createHealthMonitorForm.touched.createHealthMonitor &&
          createHealthMonitorForm.errors.createHealthMonitor
            ? createHealthMonitorForm.errors.createHealthMonitor
            : null,
      },
      ...(createHealthMonitorForm.values.createHealthMonitor === "yes"
        ? [
            {
              type: "text",
              element: "input",
              name: "name",
              label: "Name *",
              placeholder: "Enter health monitor name",
              value: createHealthMonitorForm.values.name,
              colSpan: 1,
              onChange: createHealthMonitorForm.handleChange,
              onBlur: createHealthMonitorForm.handleBlur,
              onError:
                createHealthMonitorForm.touched.name && createHealthMonitorForm.errors.name
                  ? createHealthMonitorForm.errors.name
                  : null,
            },
            {
              type: "text",
              element: "select",
              name: "type",
              label: "Type *",
              placeholder: "Select type",
              options: [
                { label: "Select type", value: "" },
                { label: "HTTP", value: "HTTP" },
                { label: "HTTPS", value: "HTTPS" },
                { label: "PING", value: "PING" },
                { label: "SCTP", value: "SCTP" },
                { label: "TCP", value: "TCP" },
                { label: "TLS-HELLO", value: "TLS-HELLO" },
                { label: "UDP-CONNECT", value: "UDP-CONNECT" },
              ],
              value: createHealthMonitorForm.values.type,
              colSpan: 1,
              onChange: createHealthMonitorForm.handleChange,
              onBlur: createHealthMonitorForm.handleBlur,
              onError:
                createHealthMonitorForm.touched.type && createHealthMonitorForm.errors.type
                  ? createHealthMonitorForm.errors.type
                  : null,
            },
            {
              type: "number",
              element: "input",
              name: "delay",
              label: "Delay (sec) *",
              placeholder: "3",
              value: createHealthMonitorForm.values.delay,
              colSpan: 1,
              onChange: createHealthMonitorForm.handleChange,
              onBlur: createHealthMonitorForm.handleBlur,
              onError:
                createHealthMonitorForm.touched.delay && createHealthMonitorForm.errors.delay
                  ? createHealthMonitorForm.errors.delay
                  : null,
            },
            {
              type: "number",
              element: "input",
              name: "max_retries",
              label: "Max Retries *",
              placeholder: "2",
              value: createHealthMonitorForm.values.max_retries,
              colSpan: 1,
              onChange: createHealthMonitorForm.handleChange,
              onBlur: createHealthMonitorForm.handleBlur,
              onError:
                createHealthMonitorForm.touched.max_retries &&
                createHealthMonitorForm.errors.max_retries
                  ? createHealthMonitorForm.errors.max_retries
                  : null,
            },
            {
              type: "number",
              element: "input",
              name: "max_retries_down",
              label: "Max Retries Down *",
              placeholder: "3",
              value: createHealthMonitorForm.values.max_retries_down,
              colSpan: 1,
              onChange: createHealthMonitorForm.handleChange,
              onBlur: createHealthMonitorForm.handleBlur,
              onError:
                createHealthMonitorForm.touched.max_retries_down &&
                createHealthMonitorForm.errors.max_retries_down
                  ? createHealthMonitorForm.errors.max_retries_down
                  : null,
            },
            {
              type: "number",
              element: "input",
              name: "timeout",
              label: "Timeout (sec) *",
              placeholder: "1",
              value: createHealthMonitorForm.values.timeout,
              colSpan: 1,
              onChange: createHealthMonitorForm.handleChange,
              onBlur: createHealthMonitorForm.handleBlur,
              onError:
                createHealthMonitorForm.touched.timeout && createHealthMonitorForm.errors.timeout
                  ? createHealthMonitorForm.errors.timeout
                  : null,
            },
            ...(createHealthMonitorForm.values.type === "HTTP" ||
            createHealthMonitorForm.values.type === "HTTPS"
              ? [
                  {
                    type: "text",
                    element: "select",
                    name: "http_method",
                    label: "HTTP Method *",
                    placeholder: "Select HTTP method",
                    options: [
                      { label: "CONNECT", value: "CONNECT" },
                      { label: "DELETE", value: "DELETE" },
                      { label: "GET", value: "GET" },
                      { label: "HEAD", value: "HEAD" },
                      { label: "OPTIONS", value: "OPTIONS" },
                      { label: "PATCH", value: "PATCH" },
                      { label: "POST", value: "POST" },
                      { label: "PUT", value: "PUT" },
                      { label: "TRACE", value: "TRACE" },
                    ],
                    value: createHealthMonitorForm.values.http_method,
                    colSpan: 1,
                    onChange: createHealthMonitorForm.handleChange,
                    onBlur: createHealthMonitorForm.handleBlur,
                    onError:
                      createHealthMonitorForm.touched.http_method &&
                      createHealthMonitorForm.errors.http_method
                        ? createHealthMonitorForm.errors.http_method
                        : null,
                  },
                  {
                    type: "text",
                    element: "input",
                    name: "expected_codes",
                    label: "Expected Codes *",
                    placeholder: "e.g., 200, 200-204",
                    value: createHealthMonitorForm.values.expected_codes,
                    colSpan: 1,
                    onChange: createHealthMonitorForm.handleChange,
                    onBlur: createHealthMonitorForm.handleBlur,
                    onError:
                      createHealthMonitorForm.touched.expected_codes &&
                      createHealthMonitorForm.errors.expected_codes
                        ? createHealthMonitorForm.errors.expected_codes
                        : null,
                  },
                  {
                    type: "text",
                    element: "input",
                    name: "url_path",
                    label: "URL Path *",
                    placeholder: "e.g., /health",
                    value: createHealthMonitorForm.values.url_path,
                    colSpan: 1,
                    onChange: createHealthMonitorForm.handleChange,
                    onBlur: createHealthMonitorForm.handleBlur,
                    onError:
                      createHealthMonitorForm.touched.url_path &&
                      createHealthMonitorForm.errors.url_path
                        ? createHealthMonitorForm.errors.url_path
                        : null,
                  },
                  {
                    type: "text",
                    element: "select",
                    name: "http_version",
                    label: "HTTP Version *",
                    placeholder: "Select HTTP version",
                    options: [
                      { label: "1.0", value: "1.0" },
                      { label: "1.1", value: "1.1" },
                    ],
                    value: createHealthMonitorForm.values.http_version,
                    colSpan: 1,
                    onChange: createHealthMonitorForm.handleChange,
                    onBlur: createHealthMonitorForm.handleBlur,
                    onError:
                      createHealthMonitorForm.touched.http_version &&
                      createHealthMonitorForm.errors.http_version
                        ? createHealthMonitorForm.errors.http_version
                        : null,
                  },
                ]
              : []),
          ]
        : []),
    ],
    [
      createHealthMonitorForm,
      createHealthMonitorForm.values,
      createHealthMonitorForm.touched,
      createHealthMonitorForm.errors,
      createHealthMonitorForm.handleChange,
      createHealthMonitorForm.handleBlur,
      poolOptions,
    ]
  );


const editHealthMonitorForm = useFormik({
  initialValues: {
    createHealthMonitor: "yes",
    type: selectedHealthMonitor?.type || "",
    delay: selectedHealthMonitor?.delay || "5",
    name: selectedHealthMonitor?.name || "",
    max_retries: selectedHealthMonitor?.max_retries || "3",
    max_retries_down: selectedHealthMonitor?.max_retries_down || "3",
    timeout: selectedHealthMonitor?.timeout || "5",
    pool_id: selectedHealthMonitor?.pools?.[0]?.id || "",
    http_method: selectedHealthMonitor?.http_method || "GET",
    expected_codes: selectedHealthMonitor?.expected_codes || "200",
    url_path: selectedHealthMonitor?.url_path || "/",
    http_version: selectedHealthMonitor?.http_version || "1.0",
  },
  enableReinitialize: true,
  validationSchema: Yup.object({
    createHealthMonitor: Yup.string()
      .required("Please select whether to update a health monitor")
      .oneOf(["yes", "no"], "Invalid choice"),
    type: Yup.string().when("createHealthMonitor", {
      is: "yes",
      then: (schema) =>
        schema
          .required("Type is required")
          .oneOf(
            ["HTTP", "HTTPS", "PING", "SCTP", "TCP", "TLS-HELLO", "UDP-CONNECT"],
            "Invalid monitor type"
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    delay: Yup.number().when("createHealthMonitor", {
      is: "yes",
      then: (schema) =>
        schema
          .required("Delay is required")
          .min(1, "Delay must be at least 1 second")
          .integer("Delay must be an integer")
          .test(
            "delay-gte-timeout",
            "Delay must be greater than or equal to Timeout",
            function (value) {
              const { timeout } = this.parent;
              return value >= parseInt(timeout);
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    name: Yup.string().when("createHealthMonitor", {
      is: "yes",
      then: (schema) => schema.required("Name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    max_retries: Yup.number().when("createHealthMonitor", {
      is: "yes",
      then: (schema) =>
        schema
          .required("Max retries is required")
          .min(1, "Max retries must be at least 1")
          .max(10, "Max retries cannot exceed 10")
          .integer("Max retries must be an integer"),
      otherwise: (schema) => schema.notRequired(),
    }),
    max_retries_down: Yup.number().when("createHealthMonitor", {
      is: "yes",
      then: (schema) =>
        schema
          .required("Max retries down is required")
          .min(1, "Max retries down must be at least 1")
          .max(10, "Max retries down must be between 1 and 10")
          .integer("Max retries down must be an integer"),
      otherwise: (schema) => schema.notRequired(),
    }),
    timeout: Yup.number().when("createHealthMonitor", {
      is: "yes",
      then: (schema) =>
        schema
          .required("Timeout is required")
          .min(0, "Timeout must be at least 0 second")
          .integer("Timeout must be an integer"),
      otherwise: (schema) => schema.notRequired(),
    }),
    pool_id: Yup.string().when("createHealthMonitor", {
      is: "yes",
      then: (schema) => schema.required("Pool is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    http_method: Yup.string().when(["createHealthMonitor", "type"], {
      is: (createHealthMonitor, type) =>
        createHealthMonitor === "yes" && ["HTTP", "HTTPS"].includes(type),
      then: (schema) => schema.required("HTTP Method is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    expected_codes: Yup.string().when(["createHealthMonitor", "type"], {
      is: (createHealthMonitor, type) =>
        createHealthMonitor === "yes" && ["HTTP", "HTTPS"].includes(type),
      then: (schema) => schema.required("Expected Codes are required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    url_path: Yup.string().when(["createHealthMonitor", "type"], {
      is: (createHealthMonitor, type) =>
        createHealthMonitor === "yes" && ["HTTP", "HTTPS"].includes(type),
      then: (schema) => schema.required("URL Path is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    http_version: Yup.string().when(["createHealthMonitor", "type"], {
      is: (createHealthMonitor, type) =>
        createHealthMonitor === "yes" && ["HTTP", "HTTPS"].includes(type),
      then: (schema) =>
        schema
          .required("HTTP Version is required")
          .oneOf(["1.0", "1.1"], "HTTP Version must be 1.0 or 1.1"),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
  onSubmit: async (values, { resetForm }) => {
    const editHealthMonitorFormData = {
      projectId,
      healthMonitorId: selectedHealthMonitor?.id,
      poolId: selectedPool?.poolId,
      type: values.type,
      delay: parseInt(values.delay),
      name: values.name,
      max_retries: parseInt(values.max_retries),
      max_retries_down: parseInt(values.max_retries_down),
      timeout: parseInt(values.timeout),
      http_method: ["HTTP", "HTTPS"].includes(values.type) ? values.http_method : undefined,
      expected_codes: ["HTTP", "HTTPS"].includes(values.type) ? values.expected_codes : undefined,
      url_path: ["HTTP", "HTTPS"].includes(values.type) ? values.url_path : undefined,
      http_version: ["HTTP", "HTTPS"].includes(values.type) ? values.http_version : undefined,
    };

    const { payload } = await dispatch(updateHealthMonitor(editHealthMonitorFormData));
    if (payload.statuscode === "00") {
      window.location.reload();
    }
  },
});

const editHealthMonitorInputs = useMemo(
  () => [
    // {
    //   type: "choice",
    //   element: "choice",
    //   name: "createHealthMonitor",
    //   label: "Update Health Monitor? *",
    //   choices: [
    //     { label: "Yes", value: "yes" },
    //     { label: "No", value: "no" },
    //   ],
    //   value: editHealthMonitorForm.values.createHealthMonitor,
    //   colSpan: 2,
    //   onChange: (value) => editHealthMonitorForm.setFieldValue("createHealthMonitor", value),
    //   onBlur: editHealthMonitorForm.handleBlur,
    //   onError:
    //     editHealthMonitorForm.touched.createHealthMonitor &&
    //     editHealthMonitorForm.errors.createHealthMonitor
    //       ? editHealthMonitorForm.errors.createHealthMonitor
    //       : null,
    // },
    ...(editHealthMonitorForm.values.createHealthMonitor === "yes"
      ? [
          {
            type: "text",
            element: "input",
            name: "name",
            label: "Name *",
            placeholder: "Enter health monitor name",
            value: editHealthMonitorForm.values.name,
            colSpan: 1,
            onChange: editHealthMonitorForm.handleChange,
            onBlur: editHealthMonitorForm.handleBlur,
            onError:
              editHealthMonitorForm.touched.name && editHealthMonitorForm.errors.name
                ? editHealthMonitorForm.errors.name
                : null,
          },
          {
            type: "text",
            element: "select",
            name: "type",
            label: "Type *",
            placeholder: "Select type",
            disabled: true,
            options: [
              { label: "Select type", value: "" },
              { label: "HTTP", value: "HTTP" },
              { label: "HTTPS", value: "HTTPS" },
              { label: "PING", value: "PING" },
              { label: "SCTP", value: "SCTP" },
              { label: "TCP", value: "TCP" },
              { label: "TLS-HELLO", value: "TLS-HELLO" },
              { label: "UDP-CONNECT", value: "UDP-CONNECT" },
            ],
            value: editHealthMonitorForm.values.type,
            colSpan: 1,
            onChange: editHealthMonitorForm.handleChange,
            onBlur: editHealthMonitorForm.handleBlur,
            onError:
              editHealthMonitorForm.touched.type && editHealthMonitorForm.errors.type
                ? editHealthMonitorForm.errors.type
                : null,
          },
          {
            type: "number",
            element: "input",
            name: "delay",
            label: "Delay (sec) *",
            placeholder: "3",
            value: editHealthMonitorForm.values.delay,
            colSpan: 1,
            onChange: editHealthMonitorForm.handleChange,
            onBlur: editHealthMonitorForm.handleBlur,
            onError:
              editHealthMonitorForm.touched.delay && editHealthMonitorForm.errors.delay
                ? editHealthMonitorForm.errors.delay
                : null,
          },
          {
            type: "number",
            element: "input",
            name: "max_retries",
            label: "Max Retries *",
            placeholder: "2",
            value: editHealthMonitorForm.values.max_retries,
            colSpan: 1,
            onChange: editHealthMonitorForm.handleChange,
            onBlur: editHealthMonitorForm.handleBlur,
            onError:
              editHealthMonitorForm.touched.max_retries &&
              editHealthMonitorForm.errors.max_retries
                ? editHealthMonitorForm.errors.max_retries
                : null,
          },
          {
            type: "number",
            element: "input",
            name: "max_retries_down",
            label: "Max Retries Down *",
            placeholder: "3",
            value: editHealthMonitorForm.values.max_retries_down,
            colSpan: 1,
            onChange: editHealthMonitorForm.handleChange,
            onBlur: editHealthMonitorForm.handleBlur,
            onError:
              editHealthMonitorForm.touched.max_retries_down &&
              editHealthMonitorForm.errors.max_retries_down
                ? editHealthMonitorForm.errors.max_retries_down
                : null,
          },
          {
            type: "number",
            element: "input",
            name: "timeout",
            label: "Timeout (sec) *",
            placeholder: "1",
            value: editHealthMonitorForm.values.timeout,
            colSpan: 1,
            onChange: editHealthMonitorForm.handleChange,
            onBlur: editHealthMonitorForm.handleBlur,
            onError:
              editHealthMonitorForm.touched.timeout && editHealthMonitorForm.errors.timeout
                ? editHealthMonitorForm.errors.timeout
                : null,
          },
          // {
          //   type: "text",
          //   element: "select",
          //   name: "pool_id",
          //   label: "Pool *",
          //   placeholder: "Select a pool",
          //   options: poolOptions,
          //   value: editHealthMonitorForm.values.pool_id,
          //   colSpan: 1,
          //   onChange: editHealthMonitorForm.handleChange,
          //   onBlur: editHealthMonitorForm.handleBlur,
          //   onError:
          //     editHealthMonitorForm.touched.pool_id && editHealthMonitorForm.errors.pool_id
          //       ? editHealthMonitorForm.errors.pool_id
          //       : null,
          // },
          ...(editHealthMonitorForm.values.type === "HTTP" ||
          editHealthMonitorForm.values.type === "HTTPS"
            ? [
                {
                  type: "text",
                  element: "select",
                  name: "http_method",
                  label: "HTTP Method *",
                  placeholder: "Select HTTP method",
                  options: [
                    { label: "CONNECT", value: "CONNECT" },
                    { label: "DELETE", value: "DELETE" },
                    { label: "GET", value: "GET" },
                    { label: "HEAD", value: "HEAD" },
                    { label: "OPTIONS", value: "OPTIONS" },
                    { label: "PATCH", value: "PATCH" },
                    { label: "POST", value: "POST" },
                    { label: "PUT", value: "PUT" },
                    { label: "TRACE", value: "TRACE" },
                  ],
                  value: editHealthMonitorForm.values.http_method,
                  colSpan: 1,
                  onChange: editHealthMonitorForm.handleChange,
                  onBlur: editHealthMonitorForm.handleBlur,
                  onError:
                    editHealthMonitorForm.touched.http_method &&
                    editHealthMonitorForm.errors.http_method
                      ? editHealthMonitorForm.errors.http_method
                      : null,
                },
                {
                  type: "text",
                  element: "input",
                  name: "expected_codes",
                  label: "Expected Codes *",
                  placeholder: "e.g., 200, 200-204",
                  value: editHealthMonitorForm.values.expected_codes,
                  colSpan: 1,
                  onChange: editHealthMonitorForm.handleChange,
                  onBlur: editHealthMonitorForm.handleBlur,
                  onError:
                    editHealthMonitorForm.touched.expected_codes &&
                    editHealthMonitorForm.errors.expected_codes
                      ? editHealthMonitorForm.errors.expected_codes
                      : null,
                },
                {
                  type: "text",
                  element: "input",
                  name: "url_path",
                  label: "URL Path *",
                  placeholder: "e.g., /health",
                  value: editHealthMonitorForm.values.url_path,
                  colSpan: 1,
                  onChange: editHealthMonitorForm.handleChange,
                  onBlur: editHealthMonitorForm.handleBlur,
                  onError:
                    editHealthMonitorForm.touched.url_path &&
                    editHealthMonitorForm.errors.url_path
                      ? editHealthMonitorForm.errors.url_path
                      : null,
                },
                {
                  type: "text",
                  element: "select",
                  name: "http_version",
                  label: "HTTP Version *",
                  placeholder: "Select HTTP version",
                  options: [
                    { label: "1.0", value: "1.0" },
                    { label: "1.1", value: "1.1" },
                  ],
                  value: editHealthMonitorForm.values.http_version,
                  colSpan: 1,
                  onChange: editHealthMonitorForm.handleChange,
                  onBlur: editHealthMonitorForm.handleBlur,
                  onError:
                    editHealthMonitorForm.touched.http_version &&
                    editHealthMonitorForm.errors.http_version
                      ? editHealthMonitorForm.errors.http_version
                      : null,
                },
              ]
            : []),
        ]
      : []),
  ],
  [
    editHealthMonitorForm,
    editHealthMonitorForm.values,
    editHealthMonitorForm.touched,
    editHealthMonitorForm.errors,
    editHealthMonitorForm.handleChange,
    editHealthMonitorForm.handleBlur,
    poolOptions,
  ]
);




const overviewDetails = [
    {
        title: "Name",
        value: loadPool?.name || "N/A",
    },

    {
        title: "Pool ID",
        value: loadPool?.id || "N/A",
    },

    {
        title: "Project ID",
        value: loadPool?.project_id || "N/A",
    },

    {
        title: "Description",
        value: loadPool?.description || "N/A",
    },

    {
        title: "Created At",
        value: loadPool?.created_at || "N/A",
    },

    {
        title: "Updated At",
        value: loadPool?.updated_at || "N/A",
    },


    {
        title: 'Session Persistence',
        value:
            Object.keys(loadPool?.session_persistence || {}).length > 0
            ? JSON.parse(JSON.stringify(loadPool.session_persistence?.type))
            : 'None',
    },


    {
        title: "Health Monitor ID",
        value: loadPool?.healthmonitor_id || "N/A",
    },


  ]



  const healthMonitorOverviewDetails = selectedHealthMonitor
  ? [
      {
        title: "Name",
        value: selectedHealthMonitor?.name || "N/A",
      },
      {
        title: "Health Monitor ID",
        value: selectedHealthMonitor?.id || "N/A",
      },
      {
        title: "Project ID",
        value: selectedHealthMonitor?.project_id || "N/A",
      },
      {
        title: "Delay",
        value: selectedHealthMonitor?.delay || "N/A",
      },
      {
        title: "Timeout",
        value: selectedHealthMonitor?.timeout|| "N/A",
      },
      {
        title: "Max Retries",
        value: selectedHealthMonitor?.max_retries || "N/A",
      },
      {
        title: "Max Retries Down",
        value: selectedHealthMonitor?.max_retries_down || "N/A",
      },
      {
        title: "HTTP Method",
        value: selectedHealthMonitor?.http_method || "N/A",
      },
      {
        title: "URL Path",
        value: selectedHealthMonitor?.url_path || "N/A",
      },
      {
        title: "Expected Codes",
        value: selectedHealthMonitor?.expected_codes || "N/A",
      },
    ]
  : [];

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const options = [
    { value: "all-health-monitors", label: "All health-monitors" },
    { value: "name", label: "Health Monitor Name" },
  ];


  
  
  const showManageSubnetModal = (key, data) => {
      setManageHealthMonitorModal(key);
      const subnetDetails = { subnetId: data.id, subnetName: data.name };
      sessionStorage.setItem("selectedHealthMonitor", JSON.stringify(subnetDetails));
  };
    
   const closeManageSubnetModal = () => {
      setManageHealthMonitorModal(false);
   };
  
  
    const toggleListenerModal = () => {
        setSelectedMonitor(!selectedHealthMonitor);
    };
  
    const toggleEditHealthMonitorModal = (listener) => {
      setSelectedMonitor(listener);
      setEditHealthMonitorModal(!editHealthMonitorModal);
    };


    const showSubnetDetailsModal = (subnet) => {
        setSelectedMonitor(subnet);
        setHealthMonitorDetailsModal(true);
    };


    const closeHealthMonitorDetailsModal = () => {
        setHealthMonitorDetailsModal(false);
        setSelectedMonitor(null);
    };



    const healthMonitorList = useHealthMonitorList(selectedProject?.id, selectedPool?.poolId);


    const healthMonitorListMap = Array.isArray(healthMonitorList)
        ? healthMonitorList.map((monitor) => ({
            id: monitor.id,
            monitorDetails: monitor,
            name: monitor.name && monitor.name.trim() !== "" ? monitor.name : "Default Health Monitor",
            protocol: monitor?.protocol || "N/A",
            type: monitor?.type || "N/A",
            pool: monitor?.default_pool_id || "N/A",
            delay: monitor?.delay || 0,
            max_retries: monitor?.max_retries || 0,
            timeout: monitor?.timeout || 0,
            operatingStatus: monitor?.operating_status || "N/A",
            provisioningStatus: monitor?.provisioning_status || "N/A",
            adminState: monitor?.admin_state_up ? "Yes" : "No",
            pools: monitor?.pools || [],
            createdAt: FormatDate(monitor.created_at),
        }))
        : [];

      const [currentPage, setCurrentPage] = useState(1);
      const entriesPerPage = 5;
    
      const filteredData = healthMonitorListMap?.filter((row) =>
        row?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        row?.provisioningStatus?.toLowerCase().includes(searchText.toLowerCase()) ||
        row?.type?.toLowerCase().includes(searchText.toLowerCase()) ||
        row?.operatingStatus?.toLowerCase().includes(searchText.toLowerCase()),
      );
    
      const indexOfLastEntry = currentPage * entriesPerPage;
      const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
      const currentEntries = filteredData.slice(
        indexOfFirstEntry,
        indexOfLastEntry,
      );
      const totalPages = Math.ceil(filteredData.length / entriesPerPage);
    
      const handlePreviousPage = () => {
        if (currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      };
    
      const handleNextPage = () => {
        if (currentPage < totalPages) {
          setCurrentPage(currentPage + 1);
        }
      };
        


      const handleServerAction = async (action, healthMonitorId) => {
        const projectId = selectedProject.id;
  
        const { payload } = await dispatch(action({ projectId, healthMonitorId }));
    
        if (payload.statuscode === "00") {
          window.location.reload();
        }
      };
    
  
    const deleteLoadHealthMonitor = (healthMonitorId) =>
      handleServerAction(deleteHealthMonitor, healthMonitorId);



    const hasHealthMonitor = (monitor) => {
        if (!selectedPool?.poolId || !healthMonitorList) return false;
        const monitorPoolId = monitor?.pools?.[0]?.id;
        return monitorPoolId === selectedPool?.poolId;
    };
  

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-2xl font-medium text-primaryBlue mb-10">Pool Details for {selectedPool?.poolName || "N/A"} </p>
      

      <div className="flex flex-col justify-start items-start w-full border-b dark:border-b-gray-500 border-b-gray-400">
            <div className="flex justify-start items-center w-full md:w-[50%]">
                {networkTabs.map((tab, i) => (
                    <button
                        key={i}
                        className={`w-full p-4 ${selectedTab === tab ? "bg-primaryBlue text-white rounded-md" : "text-primaryBlue"}  text-sm`}
                        onClick={() => setSelectedTab(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>
      </div>  

      {selectedTab === "Overview" && (
        <div className="bg-white dark:bg-slate-800 rounded-md  w-full py-6 grid gap-4 px-10 mt-8 border-t-4 border-t-primaryBlue border border-primaryBlue">
            {overviewDetails.map((item, i) => (
                <div key={i} className="w-full flex lg:flex-row flex-col justify-start lg:justify-between items-start lg:items-center gap-2 lg:gap-6">
                    <h2 className="w-full text-xl font-bold text-black dark:text-white">{item?.title}</h2>
                    <p className="w-full flex justify-start lg:justify-end text-base text-gray-600 dark:text-gray-400">{item?.value}</p>
                </div>
            ))}     
        </div>
      )}


      {selectedTab === "Health Monitors" && (
        <div>
            {healthMonitorListMap?.length > 0 ?
            <div>
                <div className="w-full flex items-center">
                    <div className="w-full  grid grid-cols-1 md:grid-cols-12 gap-4 items-end my-10">
                        <div className="md:col-span-3">
                            <Select label="Filter health monitors" name="health monitors" options={options} />
                        </div>
                        <div className="md:col-span-4">
                        <input
                            type="search"
                            name=""
                            value={searchText}
                            onChange={handleSearchChange}
                            placeholder="Search for health monitor..."
                            className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
                        />
                        </div>
                    </div>
                    <div className="w-full flex items-end justify-end">

                      <button
                          className={`cursor-pointer px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue w-fit`}
                          onClick={toggleCreateHealthMonitorModal}
                      >
                          Create Health Monitor
                      </button>
                    </div>

                </div>

                <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
                <table className="w-full text-sm text-left overflow-hidden">
                    <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                    <tr>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap w-5">
                            #
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap max-w-52">
                            Health Monitor name
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Type
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Operating Status
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Provisioning Status
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Admin State
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] border-t-2 text-sm">
                    {currentEntries.map((row, index) => (
                        <tr
                          key={index}
                          className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                        >
                        <td className="text-start py-4 px-6">{index + 1}.</td>
                        <td
                          className="text-start py-4 px-6 text-primaryBlue hover:underline cursor-pointer"
                          onClick={() => showSubnetDetailsModal(healthMonitorList.find((s) => s.id === row.id))}
                        >
                          {row.name}
                        </td>
                        <td className="text-start py-4 px-6">{row.type}</td>
                        <td className="text-start py-4 px-6">{row.operatingStatus}</td>
                        <td className="text-start py-4 px-6">{row.provisioningStatus}</td>
                        <td className="text-start py-4 px-6">{row.adminState}</td>
                        <td className="text-start py-4 px-6">
                            <button
                                className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                                onClick={() => showManageSubnetModal(index, row)}
                            >
                                Manage Health Monitor
                            </button>

                            {manageHealthMonitorModal === index && (
                                <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
                                    <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
                                    <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
                                        <div className="flex gap-2 items-center">
                                            <span className="dark:text-white">
                                                Manage "{row.name}"
                                            </span>
                                            </div>
                                            <img
                                                src={close}
                                                onClick={closeManageSubnetModal}
                                                className="h-5 cursor-pointer"
                                                alt=""
                                            />
                                        </div>
                                    <div className="p-4 grid gap-1">
                                        <div
                                            className="flex items-center justify-between hover:rounded mb-4 dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all cursor-pointer"
                                            onClick={() => toggleEditHealthMonitorModal(row)}
                                        >
                                            <span>Edit Health Monitor</span>
                                            <span>
                                                <img src={arrow} alt="" className="" />
                                            </span>
                                        </div>

                                        <div
                                            className="flex items-center justify-between hover:rounded mb-4 dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all cursor-pointer"
                                            onClick={() => {
                                                if (hasHealthMonitor(row)) {
                                                    setSelectedTab('Members');
                                                    closeManageSubnetModal();
                                                } else {
                                                    alert('This pool has no health monitor attached. Please create a health monitor first.');
                                                }
                                            }}
                                        >
                                            <span>View Pool Members</span>
                                            <span>
                                                <img src={arrow} alt="" className="" />
                                            </span>
                                        </div>
        
                                        <div
                                            className="flex justify-self-center text-red-500 rounded-md py-5 px-3 text-center cursor-pointer"
                                            onClick={() => {
                                                setDeleteHealthMonitorModal(true);
                                            }}
                                        >
                                            <span className="hover:underline">
                                                Delete Health Monitor
                                            </span>
                                        </div>
                                    </div>
                                    <Modal
                                        title={"Delete Health Monitor"}
                                        isVisible={deleteHealthMonitorModal}
                                        onClose={() => setDeleteHealthMonitorModal(false)}
                                    >
                                        <div className="text-sm text-center pt-6 text-wrap">
                                            Are you sure you want to delete{" "}
                                            <span className="underline">{row.name}</span> .
                                            This action cannot be undone.
                                        </div>
                                        <div className="grid gap-4 justify-center py-6">
                                            <button
                                                className="bg-red-500 text-white text-xs px-6 py-4 rounded-sm w-fit"
                                                onClick={() => deleteLoadHealthMonitor(row.id)}

                                            >
                                                Yes, delete {row.name}
                                            </button>
                                            <button
                                                className="text-[#1DA1F2] text-sm px-6 py-2 hover:underline"
                                                onClick={() => setDeleteHealthMonitorModal(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </Modal>
            
                                  </div>
                                </div>
                            )}

                        </td>
    
                    </tr>
                    ))}
                    </tbody>
                </table>
                </div>
                <div className="grid justify-center gap-4 md:flex md:justify-between items-center mt-4">
                <p className="text-gray-400 text-sm">
                    Showing{" "}
                    <span className="font-medium text-black dark:text-white">
                        {indexOfFirstEntry + 1}
                    </span>{" "}
                        to{" "}
                    <span className="font-medium text-black dark:text-white">
                        {Math.min(indexOfLastEntry, healthMonitorListMap?.length)}
                    </span>{" "}
                        of{" "}
                    <span className="font-medium text-black dark:text-white">
                        {healthMonitorListMap?.length}
                    </span>{" "}
                    results
                </p>
                <div className=" flex gap-3">
                    <button
                        className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === 1 ? "opacity-50" : "opacity-100"}`}
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <button
                        className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === totalPages ? "opacity-50" : "opacity-100"}`}
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
                </div>
            </div>
    
            :
    
            <>
                <div className="md:px-52 text-center grid text-gray-500 mt-32">
                    <div>It looks like you don't have health monitors attached to this pool yet. Once you create a pool, your health monitors will appear here.</div>
                    <div className="w-full flex flex-col items-center justify-center mt-4">
                        <button
                            className={`cursor-pointer px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue w-fit`}
                            onClick={toggleCreateHealthMonitorModal}
                        >
                            Create Health Monitor
                        </button>
                    </div>
                </div>
            
            </> 
            
            }
            
        </div>
      )}

        {selectedTab === "Members" && (
            <LoadBalancerMembers/>
        )}


      {healthMonitorDetailsModal && selectedHealthMonitor && (
        <div className="fixed w-full bg-black/60 top-0 left-0 z-50 h-screen">
          <div className="w-full flex h-full items-center justify-center p-4">
            <div className="bg-white dark:bg-[#141D26] dark:text-white w-full md:w-[80%] rounded h-full overflow-y-auto">
              <div className="px-6 md:px-10 py-6 border-b dark:border-b-[#c4c4c432] flex justify-between items-center">
                <span className="text-primaryBlue font-medium">Health Monitor Information:</span>
                <span onClick={closeHealthMonitorDetailsModal} className="cursor-pointer">
                  <img src={close} alt="Close" className="h-5" />
                </span>
              </div>

              <div className="py-10 grid gap-6 px-6">
                <div className="w-full flex flex-col items-start gap-4">
                  <h4 className="w-full text-black dark:text-white text-lg mb-4 border-b border-gray-400 pb-2">
                    Health Monitor Details:
                  </h4>

                  <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6">
                    {healthMonitorOverviewDetails.map((detail, index) => (
                      <div key={index} className="w-full flex flex-col items-start">
                        <h4 className="text-black dark:text-white text-lg">{detail.title}:</h4>
                        <h4 className="text-gray-800 dark:text-gray-200 font-semibold text-xl">
                            {detail.value}
                        </h4>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {createHealthMonitorModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 flex items-center justify-center p-4 bg-[rgba(0,0,0,0.5)]">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-[60%] max-h-[80%] overflow-y-scroll shadow-xl shadow-[rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Create Health Monitor</p>
              <span onClick={toggleCreateHealthMonitorModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <TabContent
                title=""
                inputs={createHealthMonitorInputs}
            />
            <div className="w-full flex flex-col justify-end items-end mt-6 pt-6 border-t border-t-gray-200">
                <Button
                buttonText="Create Health Monitor"
                buttonRole="submit"
                type="submit"
                handleClick={createHealthMonitorForm.handleSubmit}
                />
            </div>
          </div>
        </div>
      )}


    {editHealthMonitorModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 flex items-center justify-center p-4 bg-[rgba(0,0,0,0.5)]">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-[60%] max-h-[80%] overflow-y-scroll shadow-xl shadow-[rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Update Health Monitor for {selectedHealthMonitor?.name} </p>
              <span onClick={toggleEditHealthMonitorModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <TabContent
                title=""
                inputs={editHealthMonitorInputs}
            />
            <div className="w-full flex flex-col justify-end items-end mt-6 pt-6 border-t border-t-gray-200">
                <Button
                  buttonText="Update Health Monitor"
                  buttonRole="submit"
                  type="submit"
                  handleClick={editHealthMonitorForm.handleSubmit}
                />
            </div>
          </div>
        </div>
      )}



    </div>
  );
};

export default PoolDetails;
